import { useMemo, useState } from 'react';
import { SankeyChart, type SankeyLink, type SankeyNode } from '@vitness/charts';
import type { CspBucket, DerivedCategory } from '@shared/types';
import { BUCKET_TARGETS } from '@shared/groups';
import { cashFlowSplit } from '@shared/finance';
import { fmtMoney, compactMoneyFmt } from '../../lib/format';
import { bucketColour, Note, Num } from '../../components/ui';
import { useNarrow, layout } from '../../lib/useNarrow';
import { SegTabs } from '../../components/SegTabs';

// Keep in step with bucketColour in components/ui — invest is teal, because
// green is reserved for money direction.
const BUCKET_HEX: Record<CspBucket, string> = {
  fixed: '#3b82f6', invest: '#14b8a6', savings: '#8b5cf6', gfs: '#f472b6',
};

/**
 * Where the money goes, as a flow rather than a ring.
 *
 * Gross pay splits four ways — tax, pre-tax deductions, post-tax deductions, and
 * the take-home that reaches the account. Take-home then splits into the four
 * CSP buckets plus whatever's left unallocated, and each bucket splits into its
 * categories. A donut answers "what share?"; this answers "where did it go?",
 * and it's the only view that shows withholding and unallocated money at all —
 * both are invisible in a spend breakdown, and both are large.
 *
 * The deduction columns exist because lumping them into one "Tax & withholding"
 * ribbon hid the distinction that matters: an HSA leaves gross before tax and a
 * Roth 401K leaves after it, but neither ever lands in the account the budget
 * draws from. Charging them against take-home — which is what the old model did
 * — pushed committed money past take-home and made the Take-home ribbon report
 * the committed total instead of the pay that arrived.
 *
 * Depth 3 (individual categories) is dropped on narrow screens. Four columns of
 * labels do not fit on a phone, and a Sankey you have to pan is worse than a
 * simpler Sankey.
 */
export function CashFlow({
  cats, net, gross,
}: {
  cats: DerivedCategory[];
  net: number;
  gross: number;
}) {
  const narrow = useNarrow(layout.compact);
  const [detail, setDetail] = useState<'buckets' | 'categories'>('categories');
  const showCategories = detail === 'categories';

  const model = useMemo(() => {
    const split = cashFlowSplit(cats, net, gross);
    const { tax, preTax, postTax, byBucket, committed, unallocated, overAllocated } = split;

    // Gross splits two ways first, so the top level answers the only question
    // worth asking of it: how much of the paycheque you never see. Tax and the
    // two deduction kinds are the breakdown of that share, one level in — three
    // sibling ribbons at depth 1 made you add three percentages to get there.
    const deductions = tax + preTax + postTax;

    const nodes: SankeyNode[] = [
      { id: 'gross', label: 'Gross pay', depth: 0, colour: '#64748b' },
      // Pinned to `net`: the box has to be tall enough for its bucket ribbons,
      // but the caption must state the pay that actually arrived.
      { id: 'net', label: 'Take-home', depth: 1, colour: '#0ea5e9', displayValue: net },
    ];
    const links: SankeyLink[] = [{ from: 'gross', to: 'net', value: net }];

    if (deductions > 0) {
      nodes.push({ id: 'ded', label: 'Deductions', depth: 1, colour: '#dc2626' });
      links.push({ from: 'gross', to: 'ded', value: deductions });

      if (tax > 0) {
        nodes.push({ id: 'tax', label: 'Tax', depth: 2, colour: '#ef4444' });
        links.push({ from: 'ded', to: 'tax', value: tax });
      }
      if (preTax > 0) {
        nodes.push({ id: 'pre', label: 'Pre-Tax', depth: 2, colour: '#f59e0b' });
        links.push({ from: 'ded', to: 'pre', value: preTax });
      }
      if (postTax > 0) {
        nodes.push({ id: 'post', label: 'Post-Tax', depth: 2, colour: '#a855f7' });
        links.push({ from: 'ded', to: 'post', value: postTax });
      }
    }

    // Which groups sit behind each deduction column, so depth 3 can name them.
    const deductionGroups = (kind: 'pre' | 'post') =>
      cats.filter(c => c.deduction === kind && c.base > 0).sort((x, y) => y.base - x.base);

    for (const b of ['fixed', 'invest', 'savings', 'gfs'] as CspBucket[]) {
      if (byBucket[b] <= 0) continue;
      nodes.push({ id: `b-${b}`, label: BUCKET_TARGETS[b].name, depth: 2, colour: BUCKET_HEX[b] });
      links.push({ from: 'net', to: `b-${b}`, value: byBucket[b] });
    }
    if (unallocated > 0) {
      nodes.push({ id: 'b-free', label: 'Unallocated', depth: 2, colour: '#94a3b8' });
      links.push({ from: 'net', to: 'b-free', value: unallocated });
    }

    if (showCategories) {
      // Biggest first inside each bucket, and anything under 1.5% of take-home
      // rolled into "Other" — a two-pixel ribbon with a label is just noise.
      const floor = net * 0.015;
      for (const b of ['fixed', 'invest', 'savings', 'gfs'] as CspBucket[]) {
        // Take-home groups only. A deduction shares its bucket with take-home
        // lines — the 401K and the HSA are both `invest` — so including them
        // here hung them off the bucket as well as off their own deduction
        // column, drawing them twice and inflating the bucket node past the
        // total it had just been given.
        const mine = cats
          .filter(c => c.bucket === b && c.deduction === null && c.base > 0)
          .sort((x, y) => y.base - x.base);
        let rolled = 0;
        for (const c of mine) {
          if (c.base < floor && mine.length > 2) { rolled += c.base; continue; }
          nodes.push({ id: `c-${c.key}`, label: c.name, depth: 3, colour: BUCKET_HEX[b] });
          links.push({ from: `b-${b}`, to: `c-${c.key}`, value: c.base });
        }
        if (rolled > 0) {
          nodes.push({ id: `c-${b}-other`, label: 'Other', depth: 3, colour: BUCKET_HEX[b] });
          links.push({ from: `b-${b}`, to: `c-${b}-other`, value: rolled });
        }
      }

      // Name the lines inside each deduction column — "Post-Tax $670" is only
      // useful if you can see it's the 401K. Push order is free to be whatever
      // reads best here; SankeyChart sorts each column by its parents.
      for (const kind of ['pre', 'post'] as const) {
        for (const c of deductionGroups(kind)) {
          nodes.push({
            id: `d-${c.key}`, label: c.name, depth: 3,
            colour: kind === 'pre' ? '#f59e0b' : '#a855f7',
          });
          links.push({ from: kind, to: `d-${c.key}`, value: c.base });
        }
      }
    }

    return { nodes, links, tax, preTax, postTax, byBucket, committed, unallocated, overAllocated };
  }, [cats, net, gross, showCategories]);

  const rows = showCategories
    ? model.nodes.filter(n => n.depth === 3).length
    : model.nodes.filter(n => n.depth === 2).length;

  return (
    <>
      <div className="vd-flow__head">
        <Num style={{ fontSize: 11.5, color: 'var(--vn-text-muted)' }}>
          {fmtMoney(gross)} gross · {fmtMoney(model.tax)} tax
          {model.preTax > 0 && <> · {fmtMoney(model.preTax)} pre-tax</>}
          {model.postTax > 0 && <> · {fmtMoney(model.postTax)} post-tax</>}
          {' '}· {fmtMoney(net)} take-home
        </Num>
        {/* Available on every screen. Dropping it on mobile removed the one
            control that makes the diagram legible on a small screen. */}
        <SegTabs
          label="Detail level" size="sm" value={detail} onChange={setDetail}
          options={[
            { value: 'buckets', label: 'Buckets' },
            { value: 'categories', label: 'Categories' },
          ]}
        />
      </div>

      {/*
        The diagram sizes itself: column gaps are derived from the widest
        label in each column, so names and amounts always have real room, and
        the container scrolls when the result is wider than the screen —
        Monarch's behaviour, full-size and swipe sideways.
      */}
      <div className="vd-flow">
        <SankeyChart
          nodes={model.nodes}
          links={model.links}
          width={showCategories ? 1080 : 840}
          height={Math.max(340, rows * 42)}
          compact={narrow}
          minScale={narrow ? 0.95 : 0.8}
          formatValue={narrow ? compactMoneyFmt : v => fmtMoney(v)}
        />
      </div>

      {narrow && (
        <Num style={{ fontSize: 10.5, color: 'var(--vn-text-muted)', display: 'block', marginTop: 2 }}>
          ← swipe the diagram sideways →
        </Num>
      )}

      <Note>
        Ribbon thickness is dollars per month. Gross splits four ways: tax, pre-tax deductions,
        post-tax deductions, and the take-home that reaches your account. Deductions are shown
        on the gross side because they never land in the account the budget draws from — a
        401K or HSA contribution is already gone by payday, so counting it as take-home
        spending would charge it twice. Unallocated is what your Notion budget doesn't
        currently spend; it's the number that becomes savings by default.
      </Note>
      {model.overAllocated > 0 && (
        <Note tone="bad">
          Your plan commits {fmtMoney(model.overAllocated)}/mo more than take-home covers.
        </Note>
      )}

      <div className="vd-flow__legend">
        {(['fixed', 'invest', 'savings', 'gfs'] as CspBucket[]).map(b => (
          <span key={b}>
            <i style={{ background: bucketColour(b) }} />
            {BUCKET_TARGETS[b].name}
            <Num>{fmtMoney(model.byBucket[b])}</Num>
          </span>
        ))}
        <span>
          <i style={{ background: 'var(--vn-text-muted)' }} />
          Unallocated
          <Num>{fmtMoney(model.unallocated)}</Num>
        </span>
        {model.preTax > 0 && (
          <span>
            <i style={{ background: '#f59e0b' }} />
            Pre-Tax Deductions
            <Num>{fmtMoney(model.preTax)}</Num>
          </span>
        )}
        {model.postTax > 0 && (
          <span>
            <i style={{ background: '#a855f7' }} />
            Post-Tax Deductions
            <Num>{fmtMoney(model.postTax)}</Num>
          </span>
        )}
      </div>
    </>
  );
}
